import { useEffect, useState } from "react";
import { Copy, Inbox, Mic, Loader2, Swords } from "lucide-react";
import { toast } from "sonner";
import { listQuestions, patchQuestion, questionToPrep } from "@/lib/scoutApi";
import { extractApiError } from "@/lib/api";
import { copyText } from "@/lib/clipboard";

const fmt = (iso) =>
  new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

const QuestionRow = ({ q, onToggle, canPrep, onPrep }) => (
  <li
    data-testid={`scout-question-${q.id}`}
    className={`border-t border-slate-200 py-4 flex gap-4 ${q.handled ? "opacity-50" : ""}`}
  >
    <input
      type="checkbox"
      data-testid="scout-question-handled"
      checked={q.handled}
      onChange={() => onToggle(q)}
      aria-label="Mark handled"
      className="mt-1 w-4 h-4 accent-[var(--accent)] shrink-0 cursor-pointer"
    />
    <div className="flex-1 min-w-0">
      <p className={`text-sm text-[#0A192F] leading-relaxed ${q.handled ? "line-through" : ""}`}>{q.question}</p>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
        {q.topic && <span className="border border-slate-300 px-2 py-0.5">{q.topic}</span>}
        <span>{q.submitter_name || "Anonymous"}{q.submitter_email ? ` · ${q.submitter_email}` : ""}</span>
        {q.is_public_allowed && (
          <span className="inline-flex items-center gap-1 text-[var(--accent)]"><Mic className="w-3 h-3" /> OK on air</span>
        )}
        <span>{fmt(q.created_at)}</span>
        <button type="button" data-testid="scout-question-copy" onClick={() => copyText(q.question).then(() => toast.success("Copied"))}
          className="inline-flex items-center gap-1 hover:text-[#0A192F]">
          <Copy className="w-3 h-3" /> Copy
        </button>
        {canPrep && (q.in_prep_for ? (
          <span data-testid="scout-question-in-prep" className="inline-flex items-center gap-1 text-[var(--accent)]"><Swords className="w-3 h-3" /> In prep sheet</span>
        ) : (
          <button type="button" data-testid="scout-question-to-prep" onClick={() => onPrep(q)} className="inline-flex items-center gap-1 text-[var(--accent)] hover:underline">
            <Swords className="w-3 h-3" /> Add to debate prep
          </button>
        ))}
      </div>
    </div>
  </li>
);

const GroupHeader = ({ g }) => {
  const c = g.candidate;
  const open = g.questions.filter((q) => !q.handled).length;
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-3 mb-1">
      <div>
        <h3 className="font-display text-2xl text-[#0A192F] tracking-tight">{g.guest || "General · no guest named"}</h3>
        {c && (
          <p className="text-xs text-slate-500">
            {c.role} · <span className="uppercase tracking-wider">{c.status}</span>
            {c.featured ? " · featured" : ""}
          </p>
        )}
        {g.guest && !c && <p className="text-xs text-slate-400">Not in the CRM</p>}
      </div>
      <span className="font-mono text-xs text-slate-500">{open} open / {g.questions.length}</span>
    </div>
  );
};

export const QuestionInbox = ({ onCount, onPrepAdded }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    listQuestions().then(setData).catch(() => setError("Could not load questions."));
  }, []);

  useEffect(() => {
    if (data && onCount) onCount(data.unhandled);
  }, [data, onCount]);

  const toggle = async (q) => {
    try {
      const updated = await patchQuestion(q.id, !q.handled);
      setData((d) => ({
        ...d,
        unhandled: d.unhandled + (updated.handled ? -1 : 1),
        groups: d.groups.map((g) => ({
          ...g,
          questions: g.questions.map((x) => (x.id === q.id ? { ...x, ...updated } : x)),
        })),
      }));
    } catch {
      toast.error("Could not update question.");
    }
  };

  const toPrep = async (q) => {
    try {
      const res = await questionToPrep(q.id);
      setData((d) => ({
        ...d,
        groups: d.groups.map((g) => ({ ...g, questions: g.questions.map((x) => (x.id === q.id ? { ...x, in_prep_for: res.candidate_id } : x)) })),
      }));
      toast.success(res.already ? "Already in the prep sheet" : "Added to the debate prep sheet");
      onPrepAdded?.(res.candidate_id);
    } catch (err) {
      toast.error(extractApiError(err, "Could not add to prep."));
    }
  };

  if (error) return <p className="text-sm text-red-600" data-testid="scout-inbox-error">{error}</p>;
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;

  return (
    <section data-testid="scout-inbox">
      <div className="flex items-center gap-3 text-sm text-slate-600 mb-8">
        <Inbox className="w-4 h-4 text-[var(--accent)]" />
        <span data-testid="scout-inbox-summary">
          <strong className="text-[#0A192F]">{data.unhandled}</strong> open · {data.total} listener questions
        </span>
      </div>
      {data.total === 0 ? (
        <p data-testid="scout-inbox-empty" className="text-sm text-slate-500 border-t border-slate-200 pt-8">
          No listener questions yet. They land here the moment someone submits the Ask form.
        </p>
      ) : (
        <div className="space-y-12">
          {data.groups.map((g) => (
            <div key={g.guest || "__general"} data-testid={`scout-inbox-group-${(g.guest || "general").toLowerCase().replace(/\W+/g, "-")}`}>
              <GroupHeader g={g} />
              <ul>{g.questions.map((q) => <QuestionRow key={q.id} q={q} onToggle={toggle} canPrep={Boolean(g.candidate)} onPrep={toPrep} />)}</ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default QuestionInbox;
